/**
 * Global accessibility bar — floating bottom-right widget that provides:
 *  - "Listen to this page" using the browser's built-in SpeechSynthesis API
 *  - Language dropdown (30+ languages) powered by the free Google Translate
 *    widget which is loaded lazily on first open.
 *
 * The widget is intentionally minimal so it doesn't compete with the editorial
 * design. It opens into a small parchment card only when the user clicks the
 * pill.
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Headphones, Pause, ChevronUp, Check, Type } from "lucide-react";
import { GT_LANGUAGES as LANGUAGES } from "@/lib/ml";
import { useGrandparent } from "@/lib/GrandparentContext";

// Google Translate script loader (runs once).
function loadGoogleTranslate() {
  if (window.__gtLoaded) return;
  window.__gtLoaded = true;
  window.googleTranslateElementInit = function () {
    new window.google.translate.TranslateElement(
      {
        pageLanguage: "en",
        includedLanguages: LANGUAGES.filter((l) => l.code !== "en").map((l) => l.code).join(","),
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        autoDisplay: false,
      },
      "google_translate_element"
    );
  };
  const s = document.createElement("script");
  s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  document.body.appendChild(s);
}

// Set target language by controlling the hidden select#gtranslate combo.
function setTranslateLanguage(code) {
  const tryDispatch = () => {
    const combo = document.querySelector(".goog-te-combo");
    if (!combo) return false;
    combo.value = code;
    combo.dispatchEvent(new Event("change"));
    return true;
  };
  if (tryDispatch()) return;
  const start = Date.now();
  const t = setInterval(() => {
    if (tryDispatch() || Date.now() - start > 5000) clearInterval(t);
  }, 200);
}

function extractReadableText() {
  // Prefer the main content area; fall back to visible article/section text.
  const scope = document.querySelector("main") || document.body;
  const clone = scope.cloneNode(true);
  // Strip our own widget so we don't read the language names aloud.
  clone.querySelectorAll("[data-a11y-bar], nav, script, style, footer .flourish").forEach((n) => n.remove());
  const text = clone.innerText.replace(/\s+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  return text.slice(0, 8000); // safety cap
}

export default function A11yBar() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const [speaking, setSpeaking] = useState(false);
  const [supported, setSupported] = useState(true);
  const utterRef = useRef(null);
  const gp = useGrandparent();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const synth = window.speechSynthesis;
    if (!synth) {
      setSupported(false);
      return;
    }
    // Some environments expose speechSynthesis but have zero voices (headless
    // browsers, locked-down systems) — listening then silently does nothing,
    // so detect that and say so.
    const checkVoices = () => setSupported(synth.getVoices().length > 0);
    checkVoices();
    synth.addEventListener?.("voiceschanged", checkVoices);
    window.addEventListener("beforeunload", () => synth.cancel());
    return () => {
      synth.removeEventListener?.("voiceschanged", checkVoices);
      synth.cancel();
    };
  }, []);

  useEffect(() => {
    if (open) loadGoogleTranslate();
  }, [open]);

  // Allow the nav's language button to open this same panel.
  useEffect(() => {
    const openPanel = () => setOpen(true);
    window.addEventListener("ft-open-a11y", openPanel);
    return () => window.removeEventListener("ft-open-a11y", openPanel);
  }, []);

  function toggleListen() {
    if (!window.speechSynthesis) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const text = extractReadableText();
    if (!text) return;
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 0.95;
    utter.pitch = 1;
    utter.lang = lang.startsWith("zh") ? lang : lang.split("-")[0];
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    utterRef.current = utter;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
    setSpeaking(true);
  }

  function pickLanguage(code) {
    setLang(code);
    if (code === "en") {
      // Restore original page by triggering the widget's "Show original" behaviour
      setTranslateLanguage("en");
    } else {
      setTranslateLanguage(code);
    }
  }

  const currentLabel = LANGUAGES.find((l) => l.code === lang)?.label || "English";

  return (
    <>
      {/* Google Translate mount — hidden but present */}
      <div id="google_translate_element" style={{ position: "absolute", left: -9999, top: -9999 }} />

      <div
        data-a11y-bar=""
        data-testid="a11y-bar"
        className="fixed bottom-4 right-4 z-[80] flex flex-col items-end gap-2 print:hidden"
      >
        <AnimatePresence>
          {open && (
            <motion.div
              key="panel"
              initial={{ opacity: 0, y: 8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="w-[280px] max-h-[70vh] bg-[color:var(--parchment)] border border-[color:var(--sepia)]/40 shadow-[0_20px_50px_-20px_rgba(43,28,15,0.45)] overflow-hidden"
              data-testid="a11y-panel"
            >
              <div className="px-4 py-3 border-b border-[color:var(--sepia)]/25 flex items-center justify-between">
                <div className="overline text-[color:var(--sepia)]">Read · Translate</div>
                <button
                  onClick={() => setOpen(false)}
                  className="text-[color:var(--sepia)] hover:text-[color:var(--rust)]"
                  aria-label="Close"
                >
                  <ChevronUp size={14} />
                </button>
              </div>

              <div className="p-4 border-b border-[color:var(--sepia)]/25">
                <div className="overline mb-2">Listen to this page</div>
                <button
                  data-testid="a11y-listen"
                  onClick={toggleListen}
                  disabled={!supported}
                  className="btn-wood w-full justify-center !py-2 disabled:opacity-50"
                >
                  {speaking ? <Pause size={14} /> : <Headphones size={14} />}
                  {speaking ? "Stop reading" : "Listen"}
                </button>
                {!supported && (
                  <p className="mt-2 text-xs text-[color:var(--ink-soft)]" data-testid="a11y-tts-unsupported">
                    Read-aloud isn't available in this browser.
                  </p>
                )}
              </div>

              <div className="p-4 border-b border-[color:var(--sepia)]/25">
                <div className="overline mb-2">Easy reading</div>
                <button
                  data-testid="a11y-grandparent"
                  onClick={gp?.toggle}
                  className={`w-full justify-center !py-2 inline-flex items-center gap-2 border px-4 py-2 transition-colors ${
                    gp?.on
                      ? "border-[color:var(--rust)] bg-[color:var(--rust)] text-[color:var(--parchment)]"
                      : "border-[color:var(--sepia)]/40 text-[color:var(--sepia)] hover:bg-[color:var(--parchment-2)]"
                  }`}
                >
                  <Type size={14} />
                  {gp?.on ? "Larger type is ON" : "Larger type & big buttons"}
                </button>
              </div>

              <div className="p-4">
                <div className="overline mb-2">Translate the page</div>
                <div className="max-h-[36vh] overflow-y-auto -mx-1">
                  <ul>
                    {LANGUAGES.map((l) => (
                      <li key={l.code}>
                        <button
                          data-testid={`a11y-lang-${l.code}`}
                          onClick={() => pickLanguage(l.code)}
                          className={`w-full flex items-center justify-between px-3 py-1.5 text-sm hover:bg-[color:var(--parchment-2)]/60 ${
                            lang === l.code ? "text-[color:var(--rust)]" : "text-[color:var(--ink)]"
                          }`}
                        >
                          <span>{l.label}</span>
                          {lang === l.code && <Check size={12} />}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* The pill button */}
        <button
          data-testid="a11y-toggle"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-2 border border-[color:var(--sepia)]/40 bg-[color:var(--parchment)] px-4 py-2 shadow-[0_10px_28px_-10px_rgba(43,28,15,0.4)] hover:bg-[color:var(--parchment-2)] transition"
        >
          <Globe size={14} className="text-[color:var(--sepia)]" />
          <span className="font-heritage-caps text-[11px] tracking-[0.16em]">
            {currentLabel}
          </span>
          <span className="w-px h-4 bg-[color:var(--sepia)]/30" />
          <Headphones size={14} className={speaking ? "text-[color:var(--rust)]" : "text-[color:var(--sepia)]"} />
        </button>
      </div>

      {/* Suppress Google's top banner and iframes */}
      <style>{`
        .goog-te-banner-frame, .skiptranslate { display: none !important; }
        body { top: 0 !important; }
        .goog-tooltip, .goog-tooltip:hover, .goog-text-highlight { display:none !important; background:none !important; }
      `}</style>
    </>
  );
}
